import {
  HBSchema,
  HoofdstukSchema,
  PHBSchema,
  ParagraafSchema,
  SPPBSchema,
  SubparagraafSchema,
  VSPBSchema,
  VoorschriftSchema,
} from '@/api';
import { useBesluit } from '@/api/queries/besluit';
import {
  useHB,
  useHoofdstuk,
  usePHB,
  useParagraaf,
  useSPPB,
  useSubparagraaf,
  useVSPB,
  useVoorschrift,
} from '@/api/queries/objects';
import { GRONDSLAGEN, Grondslag } from '@/constants/grondslagen';
import { DateUtils } from '@/utils/dateUtils';
import { fetchAllPages } from '@/utils/fetchAllPages';
import { fetchInFilter } from '@/utils/fetchInFilter';
import { createQuery } from '@/utils/tanstack-query/createQuery';

export type VigerendVoorschrift = {
  data: VoorschriftSchema;
  VSPB: VSPBSchema;
};

export type SubparagraafLineage = {
  /** The lineage ID of the subparagraaf */
  lineageId: string;
  /** The subparagrafen in the same lineage */
  subparagrafen: SubparagraafSchema[];
  /** The sppbs in the same lineage */
  sppbs: SPPBSchema[];
  /** The voorschriften that are linked to one of the subparagrafen in the same lineage */
  voorschriften: VigerendVoorschrift[];
};

export type ParagraafLineage = {
  /** The lineage ID of the paragraaf */
  lineageId: string;
  /** The phbs in the same lineage */
  phbs: PHBSchema[];
  /** The paragrafen in the same lineage */
  paragrafen: ParagraafSchema[];
  /** The subparagrafen that are linked to one of the paragrafen in the same lineage */
  subparagraafLineages: SubparagraafLineage[];
};

export type HoofdstukLineage = {
  /** The grondslag of the hoofdstuk */
  grondslag: Grondslag;
  /** The lineage ID of the hoofdstuk */
  lineageId: string;
  /** The hbs in the same lineage */
  hbs: HBSchema[];
  /** The hoofdstukken in the same lineage */
  hoofdstukken: HoofdstukSchema[];
  /** The paragrafen that are linked to one of the hoofdstukken in the same lineage */
  paragraafLineages: ParagraafLineage[];
};

export type VigerendData = Array<{
  data: Grondslag;
  hoofdstukken: HoofdstukLineage[];
}>;

export const useVigerendData = createQuery({
  key: ['links', 'vigerend'],
  queryFn: fetchVigerendData,
});

export async function fetchVigerendData({
  date,
  bedrijfLineageId,
}: {
  date: Date;
  bedrijfLineageId: string;
}): Promise<VigerendData> {
  /**
   *
   * Fetch a besluitLineageId that is linked to the bedrijf
   *
   */
  const besluitResponse = await useBesluit.fetchQuery({
    page: 1,
    size: 1,
    filter: [
      {
        model: 'Bedrijf',
        field: 'Lineage',
        op: 'eq',
        value: bedrijfLineageId,
      },
    ],
  });

  const besluitLineageId = besluitResponse.objects?.[0]?.Lineage;

  if (!besluitLineageId) {
    throw new Error('No besluit lineage id found');
  }

  /**
   *
   * Fetch all VSPB's that are inwerking at the given date
   *
   */
  const [vspbs, voorschriften] = await Promise.all([
    useVSPB.fetchQuery({
      page: 1,
      size: 1000,
      filter: [
        {
          model: 'Voorschrift',
          field: 'timetravel',
          op: 'eq',
          value: `${DateUtils.formatForBE(date)},${besluitLineageId}`,
        },
        {
          model: 'VSPB',
          field: 'Type',
          op: 'eq',
          value: 'Eigen',
        },
      ],
      sort: [
        {
          model: 'VSPB',
          field: 'Rang',
          direction: 'asc',
        },
      ],
    }),
    fetchAllPages((page) => {
      return useVoorschrift.fetchQuery({
        page,
        size: 100,
        filter: [
          {
            model: 'Voorschrift',
            field: 'timetravel',
            op: 'eq',
            value: `${DateUtils.formatForBE(date)},${besluitLineageId}`,
          },
          {
            model: 'VSPB',
            field: 'Type',
            op: 'eq',
            value: 'Eigen',
          },
        ],
      });
    }),
  ]);

  const vspbSubparagraafIds = [
    ...new Set(vspbs.objects?.map((vspb) => vspb.Subparagraaf) ?? []),
  ];

  /**
   *
   * Fetch all parent links and objects
   *
   */
  const [sppbs, subparagrafen] = await Promise.all([
    fetchInFilter(
      ({ page, size, value }) =>
        useSPPB.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'Subparagraaf',
              op: 'in',
              value,
            },
          ],
        }),
      vspbSubparagraafIds
    ),
    fetchInFilter(
      ({ page, size, value }) =>
        useSubparagraaf.fetchQuery({
          page,
          size,
          filter: [{ model: 'Subparagraaf', field: 'ID', op: 'in', value }],
        }),
      vspbSubparagraafIds
    ),
  ]);

  const sppbParagraafIds = [
    ...new Set(sppbs.map((sppb) => sppb.Paragraaf) ?? []),
  ];

  const [phbs, paragrafen] = await Promise.all([
    fetchInFilter(
      ({ page, size, value }) =>
        usePHB.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'Paragraaf',
              op: 'in',
              value,
            },
          ],
        }),
      sppbParagraafIds
    ),
    fetchInFilter(
      ({ page, size, value }) =>
        useParagraaf.fetchQuery({
          page,
          size,
          filter: [{ field: 'ID', op: 'in', value }],
        }),
      sppbParagraafIds
    ),
  ]);

  const phbHoofdstukIds = [...new Set(phbs.map((phb) => phb.Hoofdstuk) ?? [])];

  const [hbs, hoofdstukken] = await Promise.all([
    fetchInFilter(
      ({ page, size, value }) =>
        useHB.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'Hoofdstuk',
              op: 'in',
              value,
            },
          ],
        }),
      phbHoofdstukIds
    ),
    fetchInFilter(
      ({ page, size, value }) =>
        useHoofdstuk.fetchQuery({
          page,
          size,
          filter: [
            {
              field: 'ID',
              op: 'in',
              value,
            },
          ],
        }),
      phbHoofdstukIds
    ),
  ]);

  /**
   * Group all objects by their lineage
   *
   * In the result, we might get different links and objects for the same lineage.
   * We need to group them together and show a single object per lineage.
   */

  /**
   * SubparagraafLineages
   */
  const vigerendSubparagrafen: Map<string, SubparagraafLineage> = new Map();
  subparagrafen.forEach((subparagraaf) => {
    const subparagraafSppbs = sppbs.filter(
      (sppb) => sppb.Subparagraaf === subparagraaf.ID
    );

    if (!vigerendSubparagrafen.has(subparagraaf.Lineage)) {
      vigerendSubparagrafen.set(subparagraaf.Lineage, {
        lineageId: subparagraaf.Lineage,
        subparagrafen: [],
        sppbs: [],
        voorschriften: [],
      });
    }

    const vigerendSubparagraaf = vigerendSubparagrafen.get(
      subparagraaf.Lineage
    );

    vigerendSubparagraaf!.sppbs.push(...subparagraafSppbs);
    vigerendSubparagraaf!.subparagrafen.push(subparagraaf);
  });

  /**
   * ParagraafLineages
   */
  const vigerendParagrafen: Map<string, ParagraafLineage> = new Map();
  paragrafen.forEach((paragraaf) => {
    const paragraafPhbs = phbs.filter((phb) => phb.Paragraaf === paragraaf.ID);

    if (!vigerendParagrafen.has(paragraaf.Lineage)) {
      vigerendParagrafen.set(paragraaf.Lineage, {
        lineageId: paragraaf.Lineage,
        phbs: [],
        paragrafen: [],
        subparagraafLineages: [],
      });
    }

    const paragraafLineage = vigerendParagrafen.get(paragraaf.Lineage);

    paragraafLineage!.phbs.push(...paragraafPhbs);
    paragraafLineage!.paragrafen.push(paragraaf);
  });

  /**
   * HoofdstukLineages
   */
  const vigerendHoofdstukken: Map<string, HoofdstukLineage> = new Map();
  hoofdstukken.forEach((hoofdstuk) => {
    const hoofdstukHbs = hbs.filter((hb) => hb.Hoofdstuk === hoofdstuk.ID);
    const grondslag = GRONDSLAGEN.find(
      (grondslag) => grondslag.id === hoofdstuk.Grondslag
    );

    if (!vigerendHoofdstukken.has(hoofdstuk.Lineage) && grondslag) {
      vigerendHoofdstukken.set(hoofdstuk.Lineage, {
        lineageId: hoofdstuk.Lineage,
        grondslag,
        hbs: [],
        hoofdstukken: [],
        paragraafLineages: [],
      });
    }

    const hoofdstukLineage = vigerendHoofdstukken.get(hoofdstuk.Lineage);

    hoofdstukLineage!.hbs.push(...hoofdstukHbs);
    hoofdstukLineage!.hoofdstukken.push(hoofdstuk);
  });

  /**
   *
   * Nest all lineages
   *
   */
  vigerendHoofdstukken.forEach((vigerendHoofdstuk) => {
    const vigerendHoofdstukIds = vigerendHoofdstuk.hoofdstukken.map(
      (hoofdstuk) => hoofdstuk.ID
    );

    const childVigerendParagrafen = [...vigerendParagrafen.values()].filter(
      (vigerendParagraaf) => {
        return vigerendParagraaf.phbs.some((phb) => {
          return vigerendHoofdstukIds.includes(phb.Hoofdstuk);
        });
      }
    );

    childVigerendParagrafen.forEach((vigerendParagraaf) => {
      const vigerendParagraafIds = vigerendParagraaf.paragrafen.map(
        (paragraaf) => paragraaf.ID
      );

      const childVigerendSubparagrafen = [
        ...vigerendSubparagrafen.values(),
      ].filter((vigerendSubparagraaf) => {
        return vigerendSubparagraaf.sppbs.some((sppb) => {
          return vigerendParagraafIds.includes(sppb.Paragraaf);
        });
      });

      childVigerendSubparagrafen.forEach((vigerendSubparagraaf) => {
        const subparagraafIds = vigerendSubparagraaf.subparagrafen.map(
          (subparagraaf) => subparagraaf.ID
        );

        const vigerendVoorschriften = vspbs.objects
          ?.filter((vspb) => subparagraafIds.includes(vspb.Subparagraaf))
          .reduce<VigerendVoorschrift[]>((acc, VSPB) => {
            const voorschrift = voorschriften.find(
              ({ ID }) => ID === VSPB.Voorschrift
            );

            if (voorschrift) {
              acc.push({
                VSPB,
                data: voorschrift,
              });
            }

            return acc;
          }, []);

        vigerendSubparagraaf.voorschriften = vigerendVoorschriften ?? [];
      });

      vigerendParagraaf.subparagraafLineages.push(
        ...childVigerendSubparagrafen
      );
    });

    vigerendHoofdstuk.paragraafLineages.push(...childVigerendParagrafen);
  });

  /**
   *
   * Sort the lineages by number/rang
   *
   */
  const sortedVigerendHoofdstukken = [...vigerendHoofdstukken.values()].sort(
    (a, b) => {
      return a.hoofdstukken[0]!.Nummer - b.hoofdstukken[0]!.Nummer;
    }
  );

  sortedVigerendHoofdstukken.forEach((vigerendHoofdstuk) => {
    vigerendHoofdstuk.paragraafLineages =
      vigerendHoofdstuk.paragraafLineages.sort((a, b) => {
        return a.phbs[0]!.Rang < b.phbs[0]!.Rang ? -1 : 1;
      });

    vigerendHoofdstuk.paragraafLineages.forEach((vigerendParagraaf) => {
      vigerendParagraaf.subparagraafLineages =
        vigerendParagraaf.subparagraafLineages.sort((a, b) => {
          return a.sppbs[0]!.Rang < b.sppbs[0]!.Rang ? -1 : 1;
        });
    });
  });

  /**
   *
   * Group hoofdstukken by grondslagen
   *
   */
  const grondslagen = GRONDSLAGEN.map((grondslag) => {
    const grondslagHoofdstukken = [
      ...sortedVigerendHoofdstukken.values(),
    ].filter((hoofdstuk) => {
      return hoofdstuk.hoofdstukken.some(
        (hoofdstuk) => hoofdstuk.Grondslag === grondslag.id
      );
    });

    return {
      data: grondslag,
      hoofdstukken: grondslagHoofdstukken,
    };
  }).filter((grondslag) => grondslag.hoofdstukken.length);

  return grondslagen;
}
